import { SensorData } from '@/components/lib/SensorChart.vue';
import type { HokkaidoAirportReportExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportReport';
import type { HokkaidoAirportMtxExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMtx';
import { dtFormat } from '@/lib/dateHelper';
import type {
  HokkaidoAirportSettings,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import { splitOperatingMinutesByDayNight } from '@/lib/externalReports/hokkaidoAirportOperatingTime';

interface GeoPoint {
  lat: number;
  lon: number;
}

// 地理座標間の距離を計算（メートル単位）
const calculateDistance = (from: GeoPoint, to: GeoPoint): number => {
  const R = 6371000; // 地球の半径（メートル）
  const dLat = (to.lat - from.lat) * Math.PI / 180;
  const dLon = (to.lon - from.lon) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(from.lat * Math.PI / 180) * Math.cos(to.lat * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const MAX_JAPAN_HIGHWAY_SPEED_KMH = 120; // Japanese expressway limit
const TIME_ROUND_MINUTES = 5;

interface SensorChartInputs {
  xLabels: Date[];
  vs: number[];
}
interface WorkCycleMetrics {
  workTimeTotal: string;
  standbyTimeTotal: string;
}

// MTXデータから速度を計算（速度上限の適用）
const calculateSpeedFromMtx = (mtxs: HokkaidoAirportMtxExt[]): number[] => {
  const speeds: number[] = [];

  for (let i = 0; i < mtxs.length; i++) {
    if (i === 0) {
      speeds.push(0);
      continue;
    }

    const prevMtx = mtxs[i - 1];
    const currentMtx = mtxs[i];
    const previousSpeed = speeds[i - 1] ?? 0;

    const timeDiffSeconds = (new Date(currentMtx.ts).getTime() - new Date(prevMtx.ts).getTime()) / 1000;
    if (timeDiffSeconds <= 0) {
      speeds.push(previousSpeed);
      continue;
    }

    const distanceMeters = calculateDistance(
      { lat: prevMtx.lat, lon: prevMtx.lon },
      { lat: currentMtx.lat, lon: currentMtx.lon },
    );

    const speedMetersPerSecond = distanceMeters / timeDiffSeconds;
    const speedKmh = Math.round(speedMetersPerSecond * 3.6);

    if (speedKmh > MAX_JAPAN_HIGHWAY_SPEED_KMH) {
      speeds.push(previousSpeed);
    } else {
      speeds.push(speedKmh);
    }
  }

  return speeds;
};

const formatSensorData = (
  report: HokkaidoAirportReportExt,
): SensorChartInputs => {
  const xLabels: Date[] = [];
  const vs: number[] = [];

  if (!report || !report.mtxsExt) return { xLabels: [], vs: [] };
  // MTXデータを時間順（古い順）にソート
  const sortedMtxs = [...report.mtxsExt].sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());

  // 最新MTXデータから速度を計算
  const calculatedSpeeds = calculateSpeedFromMtx(sortedMtxs);

  sortedMtxs.forEach((mtx, i) => {
    const dt = new Date(mtx.ts);
    xLabels.push(dt);
    vs.push(calculatedSpeeds[i]); // 計算された速度を使用
  });

  return {
    xLabels: xLabels,
    vs: vs,
  };
};

// センサーデータの図を描画
export function showChart(report: HokkaidoAirportReportExt): SensorData {
  const sData = new SensorData();
  const inputs = formatSensorData(report);
  sData.xLabels = inputs.xLabels;
  sData.vs = inputs.vs;
  return sData;
}

// 報告書データをCSV形式に変換
export function formatReportsToCSV(
  reports: Array<HokkaidoAirportReportExt>,
  externalReportSettings: HokkaidoAirportSettings | null,
): string {
  const headers = [
    '日付',
    '事業者名',
    '作業班名',
    '空港名',
    '待機形態',
    '対象施設',
    '作業範囲',
    '業務開始時刻',
    '業務終了時刻',
    '稼働時間（昼）',
    '稼働時間（夜）',
    '作業開始時刻',
    '作業終了時刻',
    '作業時間',
  ];
  const csvRows = [headers.join(',')];
  const companyName = externalReportSettings?.external_report_type_name ?? '';

  reports.forEach(report => {
    const { dayMinutes, nightMinutes } = splitOperatingMinutesByDayNight(report.baseDepartureTs, report.baseArrivalTs);
    // 作業時間は作業サイクルの合計にする。複数サイクルを持つ過去の報告書があるため
    const { workTimeTotal } = processWorkCycles(report);
    const row = [
      // 日付はスマホでログインした時点の業務開始日にする
      dtFormat(report.baseDepartureTs, 'yyyy/m/d'),
      companyName,
      report.hanName,
      report.airportName || '',
      report.standbyType || '',
      '',
      '',
      formatTime(report.baseDepartureTs),
      formatTime(report.baseArrivalTs),
      formatMinutesToHoursMinutes(dayMinutes, TIME_ROUND_MINUTES),
      formatMinutesToHoursMinutes(nightMinutes, TIME_ROUND_MINUTES),
      formatTime(report.operationStartTs),
      formatTime(report.operationEndTs),
      workTimeTotal,
    ];
    csvRows.push(row.join(','));
  });

  return csvRows.join('\n');
}

const formatTime = (date: Date | null): string => {
  if (!date) return '';
  return dtFormat(date, 'HH:MM');
};

// 時間差を分単位で計算
const calculateMinutesDiff = (start: Date | null, end: Date | null): number => {
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
};

const roundMinutesToInterval = (totalMinutes: number, intervalMinutes: number): number => {
  if (intervalMinutes <= 0) return totalMinutes;
  const remainder = totalMinutes % intervalMinutes;
  if (remainder === 0) return totalMinutes;

  const halfInterval = Math.floor(intervalMinutes / 2);
  if (remainder <= halfInterval) {
    return totalMinutes - remainder;
  }

  return totalMinutes + (intervalMinutes - remainder);
};

const formatMinutesToHoursMinutes = (totalMinutes: number, intervalMinutes = 1): string => {
  const roundedMinutes = intervalMinutes > 1 ? roundMinutesToInterval(totalMinutes, intervalMinutes) : totalMinutes;
  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

export function processWorkCycles(report: HokkaidoAirportReportExt): WorkCycleMetrics {
  const workCycles = report.workCycles;

  let totalWorkMinutes = 0;
  let totalStandbyMinutes = calculateMinutesDiff(report.operationEndTs, report.baseArrivalTs);

  workCycles.forEach((cycle, index) => {
    const startTime = cycle.startTime ? new Date(cycle.startTime) : null;
    const endTime = cycle.endTime ? new Date(cycle.endTime) : null;

    if (startTime && endTime) {
      totalWorkMinutes += calculateMinutesDiff(startTime, endTime);
    }

    // 次の作業周期までの待機時間を計算
    if (index < workCycles.length - 1) {
      const nextCycle = workCycles[index + 1];
      const nextStartTime = nextCycle.startTime ? new Date(nextCycle.startTime) : null;

      if (nextStartTime) {
        const standbyMinutes = calculateMinutesDiff(endTime, nextStartTime);
        if (standbyMinutes > 0) {
          totalStandbyMinutes += standbyMinutes;
        }
      }
    }
  });

  return {
    // 作業時間合計
    workTimeTotal: formatMinutesToHoursMinutes(totalWorkMinutes, TIME_ROUND_MINUTES),
    // 待機時間合計
    standbyTimeTotal: totalStandbyMinutes > 0
      ? formatMinutesToHoursMinutes(totalStandbyMinutes, TIME_ROUND_MINUTES)
      : '0:00',
  };
}

// CSVデータを生成してBlobを作成
export function createCSVBlob(csvContent: string): Blob {
  // BOMを追加してExcelで文字化けを防ぐ
  const bom = '\uFEFF';
  return new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
}
